import { call, put, select, all, takeLatest, fork } from 'redux-saga/effects';
import { gcApi } from 'config/axios';

import ViewerSocialProfileModel from 'models/user/ViewerSocialProfile';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import DateFormatter from 'utils/date/DateFormatter';
import NavigationUtil from 'utils/NavigationUtil';
import { GROUP_ROLE } from 'utils/groups/GroupsUtil';

import { CREATE_CHALLENGE_PAGE_FETCH_PARTICIPANTS, CREATE_CHALLENGE_PAGE_SUBMIT } from 'actions/ActionTypes';

import {
    createChallengePageFetchParticipantsDone,
    createChallengePageSubmitDone
} from 'actions/createChallengePageActions';
import {
    selectCreateChallengePageCreateFrom,
    selectCreateChallengePageEditingData,
    selectCreateChallengePageInitDisplayName,
    selectCreateChallengePageParticipantState
} from 'reducers/createChallengePage/createChallengePageReducer';
import { PARTICIPANT_TYPE } from 'CreateChallengePage/createChallengePageConstants';
import { fetchMultipleUserSocialProfileImage } from 'actions/userSocialProfileImageActions';

export function* fetchInitConnection() {
    const initDisplayName = yield select(selectCreateChallengePageInitDisplayName);
    if (!initDisplayName) {
        return null;
    }

    let initParticipant = null;
    try {
        initParticipant = yield call(gcApi.get, `/connection-service/connection/profile/${initDisplayName}`);
    } catch (error) {
        initParticipant = null;
    }

    return initParticipant && initParticipant.data;
}

export function* createChallengePageFetchParticipantsSaga(action) {
    try {
        const viewerDisplayName = ViewerSocialProfileModel.get('displayName');

        const initConnection = yield call(fetchInitConnection);

        const [wheelchairUsersRes, usersRes, groupsRes] = yield all([
            call(gcApi.get, `/connection-service/connection/connections?wheelchairUsers=true`),
            call(gcApi.get, `/connection-service/connection/connections`),
            call(gcApi.get, `/group-service/groups/${viewerDisplayName}?includeMembership=true&includeMemberCount=true`)
        ]);
        const wheelchairConnections = wheelchairUsersRes.data.userConnections;
        const connections = usersRes.data.userConnections;
        const wheelchairConnectionDisplayNameMap = {};
        wheelchairConnections.forEach((item) => {
            wheelchairConnectionDisplayNameMap[item.displayName] = true;
            return wheelchairConnectionDisplayNameMap;
        }, {});
        const nonWheelchairConnections = connections.filter((connection) => {
            return !wheelchairConnectionDisplayNameMap[connection.displayName];
        });
        const availableRoles = [GROUP_ROLE.OWNER, GROUP_ROLE.ADMIN];
        const groups = groupsRes.data.filter((item) => {
            return availableRoles.includes(item.membershipStatus) && item.groupMemberCount > 1;
        });
        yield put(
            createChallengePageFetchParticipantsDone({
                wheelchairMode: action.isWheelchairMode,
                initConnection,
                wheelchairConnections,
                nonWheelchairConnections,
                connections,
                groups
            })
        );
        yield fork(fetchConnectionListProfileImageAndFrameSaga, connections);
    } catch (error) {
        yield put(
            createChallengePageFetchParticipantsDone({
                error: true,
                wheelchairConnections: null,
                nonWheelchairConnections: null,
                connections: null,
                groups: null
            })
        );
    }
}

/**
 * @typedef {object} TEditingData
 * @property {number} challengeType
 * @property {string} challengeName
 * @property {string} challengeDesc
 * @property {number} participantType
 * @property {string[]} participantDisplayNames
 * @property {number} participantGroupId
 * @property {number} durationType
 * @property {Date} startDate
 * @property {Date} endDate
 */

/**
 * @typedef {object} TCreateAdhocChallengePayload
 * @property {number} socialChallengeStatusId
 * @property {number} socialChallengeActivityTypeId
 * @property {string} adHocChallengeName
 * @property {string} adHocChallengeDesc
 * @property {number} ownerUserProfileId
 * @property {string} startDate timestamp: `yyyy-MM-dd'T'HH:mm:ss.S`
 * @property {string} endDate timestamp: `yyyy-MM-dd'T'HH:mm:ss.S`
 * @property {number} durationTypeId
 * @property {number} userRanking
 * @property {{ userProfileId: number }[]} players
 */

export function* createAdhocChallengeSaga() {
    /**
     * @type {TEditingData}
     */
    const editingData = yield select(selectCreateChallengePageEditingData);
    const participantState = yield select(selectCreateChallengePageParticipantState);

    let startDate = editingData.startDate;
    let endDate = editingData.endDate;

    /**
     * @type {TCreateAdhocChallengePayload}
     */
    const payload = {
        adHocChallengeName: editingData.challengeName,
        adHocChallengeDesc: editingData.challengeDesc,
        socialChallengeActivityTypeId: editingData.challengeType,
        durationTypeId: editingData.durationType,
        startDate: DateFormatter.formatConnectTimestamp(startDate),
        endDate: DateFormatter.formatConnectTimestamp(endDate),
        userRanking: 0,
        socialChallengeStatusId: 0,
        ownerUserProfileId: ViewerSocialProfileModel.get('profileId'),
        players: []
    };

    payload.players = participantState.connections
        .filter((item) => editingData.participantDisplayNames.includes(item.displayName))
        .map((item) => ({
            userProfileId: item.userId,
            displayName: item.displayName
        }));

    try {
        const response = yield call(gcApi.post, `/adhocchallenge-service/adHocChallenge`, payload);
        yield put(createChallengePageSubmitDone());

        const createFrom = yield select(selectCreateChallengePageCreateFrom);
        AnalyticsUtil.push({
            event: 'track_event',
            category: 'Create Challenge Page Event',
            action: 'Create Adhoc Challenge V2',
            label: createFrom
        });
        NavigationUtil.navigate(`/adhoc-challenge/${response.data.uuid}`);
    } catch (error) {
        yield put(createChallengePageSubmitDone(true));
    }
}

/**
 * @typedef {object} TCreateGroupChallengePayload
 * @property {number} groupId
 * @property {number} ownerUserProfileId
 * @property {number} groupChallengeActivityTypeId
 * @property {string} groupChallengeName
 * @property {string} groupChallengeDesc
 * @property {string} startDate timestamp: `yyyy-MM-dd'T'HH:mm:ss.S`
 * @property {string} endDate timestamp: `yyyy-MM-dd'T'HH:mm:ss.S`
 * @property {number} durationTypeId
 */

export function* createGroupChallengeSaga() {
    /**
     * @type {TEditingData}
     */
    const editingData = yield select(selectCreateChallengePageEditingData);
    const participantState = yield select(selectCreateChallengePageParticipantState);

    const group = participantState.groups.find((item) => item.id === editingData.participantGroupId);

    let startDate = editingData.startDate;
    let endDate = editingData.endDate;

    /**
     * @type {TCreateGroupChallengePayload}
     */
    const payload = {
        groupChallengeName: editingData.challengeName,
        groupChallengeDesc: editingData.challengeDesc,
        groupChallengeActivityTypeId: editingData.challengeType,
        durationTypeId: editingData.durationType,
        startDate: DateFormatter.formatConnectTimestamp(startDate),
        endDate: DateFormatter.formatConnectTimestamp(endDate),
        ownerUserProfileId: ViewerSocialProfileModel.get('profileId'),
        groupId: group.id
    };

    try {
        yield call(gcApi.post, `/groupchallenge-service/group/${group.id}/groupchallenge`, payload);
        yield put(createChallengePageSubmitDone());

        const createFrom = yield select(selectCreateChallengePageCreateFrom);
        AnalyticsUtil.push({
            event: 'track_event',
            category: 'Create Challenge Page Event',
            action: 'Create Group Challenge V2',
            label: createFrom
        });
        NavigationUtil.navigate(`/group/${group.id}/challenges/challengeCreated`);
    } catch (error) {
        yield put(createChallengePageSubmitDone(true));
    }
}

export function* createChallengePageSubmitSaga() {
    const { participantType } = yield select(selectCreateChallengePageEditingData);
    if (participantType === PARTICIPANT_TYPE.CONNECTIONS) {
        yield fork(createAdhocChallengeSaga);
        return;
    }
    if (participantType === PARTICIPANT_TYPE.GROUP) {
        yield fork(createGroupChallengeSaga);
        return;
    }
    yield put(createChallengePageSubmitDone(true));
}

/// section: fetching profile image and frame

/**
 * handle comments profile images and frames
 * @param {Object[]} responseData
 * @param {number} responseData[].userId
 */
function* fetchConnectionListProfileImageAndFrameSaga(responseData) {
    if (!responseData?.length) {
        return;
    }
    const userIds = responseData.map((item) => item.userId);
    yield fork(fetchProfileImageAndFrameSaga, userIds);
}

function* fetchProfileImageAndFrameSaga(userIds) {
    if (!userIds || !userIds.length) {
        return;
    }
    yield put(fetchMultipleUserSocialProfileImage(userIds));
}

/// end section: fetching profile image and frame

export const createChallengePageSagas = [
    takeLatest(CREATE_CHALLENGE_PAGE_FETCH_PARTICIPANTS, createChallengePageFetchParticipantsSaga),
    takeLatest(CREATE_CHALLENGE_PAGE_SUBMIT, createChallengePageSubmitSaga)
];
